import React, { useEffect, useState } from 'react';
import './geofence.scss';

import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import {
	Button,
	ListTable,
	ModalConfirmation,
	Typography,
} from 'design_system/src';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';

import { GeofenceStatus } from 'models/geofence/enums/geofence.enum';
import { Geofence } from 'models/geofence/interfaces/geofence.interface';
import {
	fetchGeofences,
	updateGeofence,
} from 'models/geofence/controllers/geofence.controller';
import EditGeofence from './edit/EditGeofence';

function Geofences() {
	const [geofences, setGeofences] = useState<Geofence[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedGeofence, setSelectedGeofence] = useState<
		Geofence | undefined
	>();
	const [openEditModal, setOpenEditModal] = useState(false);
	const [openStatusModal, setOpenStatusModal] = useState(false);
	const [statusTarget, setStatusTarget] = useState<{
		geofence: Geofence;
		nextStatus: GeofenceStatus;
	} | null>(null);

	const loadGeofences = async () => {
		setLoading(true);
		try {
			const res = await fetchGeofences({ limit: -1 });
			const list = Array.isArray(res) ? res : res.data;
			setGeofences(list || []);
		} catch (error: unknown) {
			toasterNotification({
				msg: error instanceof Error ? error.message : 'Error loading geofences',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadGeofences();
	}, []);

	const handleCreate = () => {
		setSelectedGeofence(undefined);
		setOpenEditModal(true);
	};

	const handleEdit = (geofence: Geofence) => {
		setSelectedGeofence(geofence);
		setOpenEditModal(true);
	};

	const handleCloseModal = () => {
		setOpenEditModal(false);
		setSelectedGeofence(undefined);
	};

	const handleSaveSuccess = () => {
		loadGeofences();
		handleCloseModal();
	};

	const handleAskToggleStatus = (geofence: Geofence) => {
		const nextStatus =
			geofence.status === GeofenceStatus.ACTIVE
				? GeofenceStatus.INACTIVE
				: GeofenceStatus.ACTIVE;

		setStatusTarget({ geofence, nextStatus });
		setOpenStatusModal(true);
	};

	const handleConfirmToggleStatus = async () => {
		if (!statusTarget) return;

		try {
			await updateGeofence(statusTarget.geofence._id, {
				status: statusTarget.nextStatus,
			});
			toasterNotification({
				msg: 'Geofence status updated successfully',
				toasterType: toasterOptions.success,
			});
			setOpenStatusModal(false);
			setStatusTarget(null);
			loadGeofences();
		} catch (error: unknown) {
			toasterNotification({
				msg: extractErrorMessage(error) || 'Error updating geofence status',
				toasterType: toasterOptions.error,
			});
		}
	};

	const cols = [
		{ label: 'Name', attribute: 'name' as keyof Geofence },
		{
			label: 'Status',
			render: (
				row: Geofence,
				_index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => <BaseTypography>{row.status}</BaseTypography>,
		},
		{
			label: 'Radius (m)',
			render: (
				row: Geofence,
				_index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => <BaseTypography>{row.radiusMeters}</BaseTypography>,
		},
		{
			label: 'Lat',
			render: (
				row: Geofence,
				_index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => (
				<BaseTypography>{row.center?.coordinates?.[1] ?? '—'}</BaseTypography>
			),
		},
		{
			label: 'Lng',
			render: (
				row: Geofence,
				_index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => (
				<BaseTypography>{row.center?.coordinates?.[0] ?? '—'}</BaseTypography>
			),
		},
		{
			label: 'Actions',
			render: (row: Geofence) => {
				const toggleLabel =
					row.status === GeofenceStatus.ACTIVE ? 'Deactivate' : 'Activate';

				return (
					<div style={{ display: 'flex', gap: 8 }}>
						<Button
							variant="outline"
							scale="mini"
							onClick={() => handleEdit(row)}
						>
							<Typography scale="xsmall" weight="400">
								Edit
							</Typography>
						</Button>
						<Button
							variant="outline"
							scale="mini"
							color={row.status === GeofenceStatus.ACTIVE ? 'red' : 'green'}
							onClick={() => handleAskToggleStatus(row)}
						>
							<Typography scale="xsmall" weight="400">
								{toggleLabel}
							</Typography>
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div className="geofence-container">
			<div className="geofence-header">
				<Typography scale="heading3" weight="600">
					Geofences
				</Typography>
				<Button variant="principal" icon={<AddCircle />} onClick={handleCreate}>
					Create Geofence
				</Button>
			</div>

			{loading ? (
				<div style={{ padding: 24, textAlign: 'center' }}>
					<Typography scale="medium" weight="400">
						Loading geofences...
					</Typography>
				</div>
			) : (
				<ListTable
					selectId="_id"
					canSelect={false}
					data={geofences}
					cols={cols}
				/>
			)}

			{openEditModal && (
				<EditGeofence
					geofence={selectedGeofence}
					onClose={handleCloseModal}
					onSave={handleSaveSuccess}
				/>
			)}

			<ModalConfirmation
				openConfirmation={openStatusModal}
				handleClose={() => {
					setOpenStatusModal(false);
					setStatusTarget(null);
				}}
				onConfirmationCallBack={handleConfirmToggleStatus}
				copyConfirm="Yes"
				copyReject="No"
				widthDesktop={30}
				title={
					<Typography scale="heading4" weight="600" className="text_center">
						Confirm status change
					</Typography>
				}
				subtitle={
					<Typography scale="small" weight="400" className="text_center">
						{statusTarget
							? `Set "${statusTarget.geofence.name}" to ${statusTarget.nextStatus}?`
							: ''}
					</Typography>
				}
			/>
		</div>
	);
}

export default Geofences;
